import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import styled from 'styled-components';

import { MODAL_SIZE } from '@CommonConstants';
import { ACHIEVEMENT_MODAL_KEYS } from '@Services/Editor/Constants';

import {
  addEmptyAchievement,
  setAchievementModalStateByType,
  setAllAchievementText,
  setAchievementTextByType,
  setAchievementId,
  duplicateAchievement,
  deleteAchievement,
  deleteAllEmptyPropertyAchievement,
  deleteAchievementId,
  useProcessKeysByAchievementId,
  deleteAchievementIds,
} from '@Services/Editor';

import Modal from '@Components/Common/Modal';
import TX from '@Components/Common/Text';
import SnackBar from '@Components/Common/SnackBar';

import {
  ItemCell,
  Bottom,
  NoticeDeleteAchievementModal,
  NoticeNewLinkedAchievementModal,
} from '@Components/Editor/Achievement';

const Achievement = () => {
  const dispatch = useDispatch();
  const {
    modal: { isAchievementModalVisible, isUsingAchievementChip, isUsingEditMode },
    chip: { processId, choiceId, resultId },
    achievement,
  } = useSelector(({ editor }) => editor.achievements);
  const processes = useSelector(({ editor }) => editor.processes);
  const processKeysByAchievementId = useSelector(useProcessKeysByAchievementId, shallowEqual);
  const itemCellContainerRef = useRef(null);
  const snackBarRef = useRef(null);

  const [isNoticeDeleteModalVisible, setIsNoticeDeleteModalVisible] = useState(false);
  const [isNoticeNewLinkedModalVisible, setIsNoticeNewLinkedModalVisible] = useState(false);
  const [selectedAchievementId, setSelectedAchievementId] = useState(null);
  const [tempAchievement, setTempAchievement] = useState(achievement);

  const isNoAchievement = !achievement.length;

  const handleSetTempAchievementText = (achievementId, type, text) => {
    setTempAchievement(prevTempAchievement =>
      prevTempAchievement.map(item => (item.id === achievementId ? { ...item, [type]: text } : item)),
    );
  };
  const handleDispatchAchievementText = useCallback(
    (achievementId, type, text) => {
      dispatch(setAchievementTextByType({ achievementId, type, text }));
    },
    [dispatch],
  );

  const handleChangeAchievementText = (achievementId, type, event) => {
    const {
      target: { value },
    } = event;
    if (isUsingAchievementChip) {
      handleSetTempAchievementText(achievementId, type, value);
    } else {
      handleSetTempAchievementText(achievementId, type, value);
      handleDispatchAchievementText(achievementId, type, value);
    }
  };

  const handleIsNoticeDeleteModalVisible = useCallback(() => {
    setIsNoticeDeleteModalVisible(!isNoticeDeleteModalVisible);
  }, [isNoticeDeleteModalVisible]);

  const handleIsNoticeNewLinkedModalVisible = useCallback(() => {
    setIsNoticeNewLinkedModalVisible(!isNoticeNewLinkedModalVisible);
  }, [isNoticeNewLinkedModalVisible]);

  const handleChangeIsUsingAchievementChip = useCallback(() => {
    dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.USING_CHIP }));
  }, [dispatch]);

  const handleChangeIsUsingEditMode = useCallback(() => {
    dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.USING_EDIT_MODE }));
  }, [dispatch]);

  const handleIsAchievementModalVisible = useCallback(() => {
    if (isUsingAchievementChip) {
      handleChangeIsUsingAchievementChip();
    }

    if (isUsingEditMode) {
      handleChangeIsUsingEditMode();
    }

    dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.MODAL_VISIBLE }));
  }, [isUsingAchievementChip, isUsingEditMode, dispatch]);

  const handleSetSelectedAchievementId = useCallback(
    achievementId => {
      setSelectedAchievementId(achievementId);
    },
    [selectedAchievementId],
  );

  const handleSetAchievementId = useCallback(
    achievementId => {
      dispatch(setAchievementId({ processId, choiceId, resultId, achievementId }));
    },
    [processId, choiceId, resultId, dispatch],
  );

  const generateDuplicateAchievement = (achievementId, prevAchievement) => {
    // const targetAchievement = prevAchievement[achievementId];
    // const newAchievementId = prevAchievement.length;

    const findTargetAchievement = prevAchievement.find(({ id }) => id === achievementId);

    const maxId = Math.max(...prevAchievement.map(({ id }) => id));

    const newAchievementId = maxId + 1;

    return { ...findTargetAchievement, id: newAchievementId };
  };

  const handleDuplicateAchievementClick = useCallback(
    achievementId => {
      dispatch(duplicateAchievement({ achievementId }));
      setTempAchievement(prevAchievement => [
        ...prevAchievement,
        generateDuplicateAchievement(achievementId, prevAchievement),
      ]);
    },
    [tempAchievement, dispatch],
  );

  const handleDeleteAllEmptyPropertyAchievement = useCallback(() => {
    dispatch(deleteAllEmptyPropertyAchievement());
  }, [dispatch]);

  const handleDeleteAchievementId = useCallback(
    (targetProcess, achievementId) => {
      if (!targetProcess) {
        dispatch(deleteAchievementId({ processId, choiceId, resultId }));
      } else {
        // const { processId: prevProcessId, choiceId: prevChoiceId, resultId: prevResultId } = targetProcess;

        // dispatch(deleteAchievementId({ processId: prevProcessId, choiceId: prevChoiceId, resultId: prevResultId }));
        dispatch(deleteAchievementIds({ achievementId }));
      }
    },
    [processId, choiceId, resultId, dispatch],
  );

  const handleDeleteAchievement = useCallback(
    achievementId => {
      // const targetLinkedProcess = processKeysByAchievementId[achievementId];
      // if (targetLinkedProcess) {
      //   handleDeleteAchievementId(targetLinkedProcess, achievementId);
      // }
      dispatch(deleteAchievement({ achievementId }));
      setTempAchievement(prevAchievement => prevAchievement.filter(({ id }) => id !== achievementId));
    },
    [processKeysByAchievementId, tempAchievement, dispatch],
  );

  const handleDeleteAchievementClick = useCallback(
    achievementId => {
      if (isNoticeDeleteModalVisible) {
        handleIsNoticeDeleteModalVisible();
      }
      handleDeleteAchievement(achievementId);
      dispatch(deleteAchievementIds({ achievementId }));

      snackBarRef.current.show({
        text: '업적이 삭제되었습니다.',
      });
    },
    [isNoticeDeleteModalVisible],
  );

  const handleEditModeButtonClick = useCallback(() => {
    if (isUsingEditMode) {
      dispatch(setAllAchievementText({ achievement: tempAchievement }));
    }

    dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.USING_EDIT_MODE }));
  }, [isUsingEditMode, tempAchievement, dispatch]);

  const generateEmptyAchievement = prevAchievement => {
    const maxId = Math.max(...prevAchievement.map(({ id }) => id));
    const newAchievementId = prevAchievement.length ? maxId + 1 : 0;

    return {
      id: newAchievementId,
      title: '',
      description: '',
      condition: '',
    };
  };

  const handleAddAchievementButtonClick = useCallback(() => {
    dispatch(addEmptyAchievement());
    setTempAchievement(prevAchievement => [...prevAchievement, generateEmptyAchievement(prevAchievement)]);
  }, [tempAchievement, dispatch]);

  // const getIsAlreadyLinkedAchievement = achievementId => {
  //   if (!processKeysByAchievementId[achievementId]) {
  //     return false;
  //   }

  //   const {
  //     processId: linkedProcessId,
  //     choiceId: linkedChoiceId,
  //     resultId: linkedResultId,
  //   } = processKeysByAchievementId[achievementId];

  //   return (
  //     processKeysByAchievementId[achievementId] &&
  //     (linkedProcessId !== processId ||
  //       (linkedProcessId === processId && linkedChoiceId !== choiceId) ||
  //       (linkedProcessId === processId && linkedChoiceId === choiceId && linkedResultId !== resultId))
  //   );
  // };

  const getIsCurrentLinkedAchievement = achievementId => {
    // console.log('processes:', processes);
    // console.log('processId, choiceId, resultId:', processId, choiceId, resultId);

    const findProcess = processes[processId].choices[choiceId].results;

    const findResult = findProcess.find(v => v.resultId === resultId);

    // console.log('findResult:', findResult);

    // console.log('achievementId:', achievementId);

    const hasAchievementId = findResult.newAchievementId === achievementId;
    return hasAchievementId;
    // processKeysByAchievementId[achievementId] &&
    // processKeysByAchievementId[achievementId].processId === processId &&
    // processKeysByAchievementId[achievementId].resultId === resultId &&
    // processKeysByAchievementId[achievementId].choiceId === choiceId
  };

  const handleAchievementItemCellClick = achievementId => {
    if (!isUsingAchievementChip || isUsingEditMode) {
      return;
    }

    handleSetSelectedAchievementId(achievementId);

    // const isAlreadyLinkedAchievement = getIsAlreadyLinkedAchievement(achievementId);
    const isCurrentLinkedAchievement = getIsCurrentLinkedAchievement(achievementId);

    // if (isAlreadyLinkedAchievement) {
    //   handleIsNoticeNewLinkedModalVisible();
    //   return;
    // }

    if (isCurrentLinkedAchievement) {
      handleDeleteAchievementId();
      handleIsAchievementModalVisible();
      return;
    }

    handleSetAchievementId(achievementId);
    handleIsAchievementModalVisible();
  };

  const handleNewLinkedAchievementClick = () => {
    const prevLinkedAchievement = processKeysByAchievementId[selectedAchievementId];

    handleDeleteAchievementId(prevLinkedAchievement);
    handleSetAchievementId(selectedAchievementId);
    handleIsNoticeNewLinkedModalVisible();
  };

  const scrollToBottom = () => {
    const { current } = itemCellContainerRef;

    if (current) {
      current.scrollTop = current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [achievement]);

  useEffect(() => {
    setTempAchievement(achievement);

    return () => {
      if (isAchievementModalVisible) {
        handleDeleteAllEmptyPropertyAchievement();
      }
    };
  }, [isAchievementModalVisible]);

  return (
    <Modal
      visible={isAchievementModalVisible}
      size={MODAL_SIZE.BIG}
      handleCloseButtonClick={handleIsAchievementModalVisible}
    >
      {isNoAchievement ? (
        <NoAchievementContainer>
          <NoAchievementText>
            {isUsingAchievementChip && '편집 >'} 업적 추가 버튼을 눌러 새 업적을 만드세요
          </NoAchievementText>
        </NoAchievementContainer>
      ) : (
        <InnerContainer ref={itemCellContainerRef}>
          {tempAchievement.map(({ id, title, description, condition }) => (
            <ItemCell
              key={id}
              isUsingEditMode={isUsingEditMode}
              isUsingAchievementChip={isUsingAchievementChip}
              achievementId={id}
              title={title}
              description={description}
              condition={condition}
              processKeysByAchievementId={processKeysByAchievementId}
              getIsCurrentLinkedAchievement={getIsCurrentLinkedAchievement}
              handleChangeAchievementText={handleChangeAchievementText}
              handleDuplicateAchievementClick={handleDuplicateAchievementClick}
              handleDeleteAchievementClick={handleDeleteAchievementClick}
              handleSetSelectedAchievementId={handleSetSelectedAchievementId}
              handleAchievementItemCellClick={handleAchievementItemCellClick}
              handleIsNoticeDeleteModalVisible={handleIsNoticeDeleteModalVisible}
            />
          ))}
        </InnerContainer>
      )}

      <Bottom
        isUsingAchievementChip={isUsingAchievementChip}
        isUsingEditMode={isUsingEditMode}
        achievement={tempAchievement}
        handleEditModeButtonClick={handleEditModeButtonClick}
        handleAddAchievementButtonClick={handleAddAchievementButtonClick}
      />

      <NoticeDeleteAchievementModal
        achievementId={selectedAchievementId}
        isNoticeDeleteModalVisible={isNoticeDeleteModalVisible}
        handleIsNoticeDeleteModalVisible={handleIsNoticeDeleteModalVisible}
        handleDeleteAchievementClick={handleDeleteAchievementClick}
      />

      <NoticeNewLinkedAchievementModal
        isNoticeNewLinkedModalVisible={isNoticeNewLinkedModalVisible}
        handleIsNoticeNewLinkedModalVisible={handleIsNoticeNewLinkedModalVisible}
        handleNewLinkedAchievementClick={handleNewLinkedAchievementClick}
      />

      <SnackBar ref={snackBarRef} width={300} />
    </Modal>
  );
};

const InnerContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  width: calc(100% + 40px);
  height: 452px;

  padding: 4px 20px;

  overflow-y: scroll;
  overflow-x: visible;
  gap: 5px 0px;
  margin-top: -16px;
  padding-bottom: 60px;
`;

const NoAchievementContainer = styled.div`
  position: absolute;
  top: 0;

  height: 100%;
`;

const NoAchievementText = styled(TX.FootnoteM)`
  color: ${({ theme }) => theme.colors.ui_on_4};

  height: 100%;
`;

export default Achievement;

// import React, { useRef, useState, useEffect, useCallback } from 'react';
// import { useSelector, useDispatch, shallowEqual } from 'react-redux';
// import styled from 'styled-components';

// import { MODAL_SIZE } from '@CommonConstants';
// import { ACHIEVEMENT_MODAL_KEYS } from '@Services/Editor/Constants';

// import {
//   addEmptyAchievement,
//   setAchievementModalStateByType,
//   setAllAchievementText,
//   setAchievementTextByType,
//   setAchievementId,
//   duplicateAchievement,
//   deleteAchievement,
//   deleteAllEmptyPropertyAchievement,
//   deleteAchievementId,
//   useProcessKeysByAchievementId,
// } from '@Services/Editor';

// import Modal from '@Components/Common/Modal';
// import TX from '@Components/Common/Text';
// import SnackBar from '@Components/Common/SnackBar';

// import {
//   ItemCell,
//   Bottom,
//   NoticeDeleteAchievementModal,
//   NoticeNewLinkedAchievementModal,
// } from '@Components/Editor/Achievement';

// const Achievement = () => {
//   const dispatch = useDispatch();
//   const {
//     modal: { isAchievementModalVisible, isUsingAchievementChip, isUsingEditMode },
//     chip: { processId, choiceId, resultId },
//     achievement,
//   } = useSelector(({ editor }) => editor.achievements);
//   const processKeysByAchievementId = useSelector(useProcessKeysByAchievementId, shallowEqual);
//   const itemCellContainerRef = useRef(null);
//   const snackBarRef = useRef(null);

//   const [isNoticeDeleteModalVisible, setIsNoticeDeleteModalVisible] = useState(false);
//   const [isNoticeNewLinkedModalVisible, setIsNoticeNewLinkedModalVisible] = useState(false);
//   const [selectedAchievementId, setSelectedAchievementId] = useState(null);
//   const [tempAchievement, setTempAchievement] = useState(achievement);

//   const isNoAchievement = !achievement.length;

//   const handleSetTempAchievementText = (achievementId, type, text) => {
//     setTempAchievement(prevTempAchievement =>
//       prevTempAchievement.map(item => (item.id === achievementId ? { ...item, [type]: text } : item)),
//     );
//   };
//   const handleDispatchAchievementText = useCallback(
//     (achievementId, type, text) => {
//       dispatch(setAchievementTextByType({ achievementId, type, text }));
//     },
//     [dispatch],
//   );

//   const handleChangeAchievementText = (achievementId, type, event) => {
//     const {
//       target: { value },
//     } = event;
//     if (isUsingAchievementChip) {
//       handleSetTempAchievementText(achievementId, type, value);
//     } else {
//       handleSetTempAchievementText(achievementId, type, value);
//       handleDispatchAchievementText(achievementId, type, value);
//     }
//   };

//   const handleIsNoticeDeleteModalVisible = useCallback(() => {
//     setIsNoticeDeleteModalVisible(!isNoticeDeleteModalVisible);
//   }, [isNoticeDeleteModalVisible]);

//   const handleIsNoticeNewLinkedModalVisible = useCallback(() => {
//     setIsNoticeNewLinkedModalVisible(!isNoticeNewLinkedModalVisible);
//   }, [isNoticeNewLinkedModalVisible]);

//   const handleChangeIsUsingAchievementChip = useCallback(() => {
//     dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.USING_CHIP }));
//   }, [dispatch]);

//   const handleChangeIsUsingEditMode = useCallback(() => {
//     dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.USING_EDIT_MODE }));
//   }, [dispatch]);

//   const handleIsAchievementModalVisible = useCallback(() => {
//     if (isUsingAchievementChip) {
//       handleChangeIsUsingAchievementChip();
//     }

//     if (isUsingEditMode) {
//       handleChangeIsUsingEditMode();
//     }

//     dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.MODAL_VISIBLE }));
//   }, [isUsingAchievementChip, isUsingEditMode, dispatch]);

//   const handleSetSelectedAchievementId = useCallback(
//     achievementId => {
//       setSelectedAchievementId(achievementId);
//     },
//     [selectedAchievementId],
//   );

//   const handleSetAchievementId = useCallback(
//     achievementId => {
//       dispatch(setAchievementId({ processId, choiceId, resultId, achievementId }));
//     },
//     [processId, choiceId, resultId, dispatch],
//   );

//   const generateDuplicateAchievement = (achievementId, prevAchievement) => {
//     const targetAchievement = prevAchievement[achievementId];
//     const newAchievementId = prevAchievement.length;

//     return { ...targetAchievement, id: newAchievementId };
//   };

//   const handleDuplicateAchievementClick = useCallback(
//     achievementId => {
//       dispatch(duplicateAchievement({ achievementId }));
//       setTempAchievement(prevAchievement => [
//         ...prevAchievement,
//         generateDuplicateAchievement(achievementId, prevAchievement),
//       ]);
//     },
//     [tempAchievement, dispatch],
//   );

//   const handleDeleteAllEmptyPropertyAchievement = useCallback(() => {
//     dispatch(deleteAllEmptyPropertyAchievement());
//   }, [dispatch]);

//   const handleDeleteAchievementId = useCallback(
//     targetProcess => {
//       if (!targetProcess) {
//         dispatch(deleteAchievementId({ processId, choiceId, resultId }));
//       } else {
//         const { processId: prevProcessId, choiceId: prevChoiceId, resultId: prevResultId } = targetProcess;

//         dispatch(deleteAchievementId({ processId: prevProcessId, choiceId: prevChoiceId, resultId: prevResultId }));
//       }
//     },
//     [processId, choiceId, resultId, dispatch],
//   );

//   const handleDeleteAchievement = useCallback(
//     achievementId => {
//       const targetLinkedProcess = processKeysByAchievementId[achievementId];
//       console.log('targetLinkedProcess:', targetLinkedProcess);
//       if (targetLinkedProcess) {
//         handleDeleteAchievementId(targetLinkedProcess);
//       }

//       dispatch(deleteAchievement({ achievementId }));
//       setTempAchievement(prevAchievement => prevAchievement.filter(({ id }) => id !== achievementId));
//     },
//     [processKeysByAchievementId, tempAchievement, dispatch],
//   );

//   const handleDeleteAchievementClick = useCallback(
//     achievementId => {
//       if (isNoticeDeleteModalVisible) {
//         handleIsNoticeDeleteModalVisible();
//       }

//       handleDeleteAchievement(achievementId);
//       snackBarRef.current.show({
//         text: '업적이 삭제되었습니다.',
//       });
//     },
//     [isNoticeDeleteModalVisible],
//   );

//   const handleEditModeButtonClick = useCallback(() => {
//     if (isUsingEditMode) {
//       dispatch(setAllAchievementText({ achievement: tempAchievement }));
//     }

//     dispatch(setAchievementModalStateByType({ type: ACHIEVEMENT_MODAL_KEYS.USING_EDIT_MODE }));
//   }, [isUsingEditMode, tempAchievement, dispatch]);

//   const generateEmptyAchievement = prevAchievement => {
//     return {
//       id: prevAchievement.length,
//       title: '',
//       description: '',
//       condition: '',
//     };
//   };

//   const handleAddAchievementButtonClick = useCallback(() => {
//     dispatch(addEmptyAchievement());
//     setTempAchievement(prevAchievement => [...prevAchievement, generateEmptyAchievement(prevAchievement)]);
//   }, [tempAchievement, dispatch]);

//   const getIsAlreadyLinkedAchievement = achievementId => {
//     if (!processKeysByAchievementId[achievementId]) {
//       return false;
//     }

//     const {
//       processId: linkedProcessId,
//       choiceId: linkedChoiceId,
//       resultId: linkedResultId,
//     } = processKeysByAchievementId[achievementId];

//     return (
//       processKeysByAchievementId[achievementId] &&
//       (linkedProcessId !== processId ||
//         (linkedProcessId === processId && linkedChoiceId !== choiceId) ||
//         (linkedProcessId === processId && linkedChoiceId === choiceId && linkedResultId !== resultId))
//     );
//   };

//   const getIsCurrentLinkedAchievement = achievementId => {
//     return (
//       processKeysByAchievementId[achievementId] &&
//       processKeysByAchievementId[achievementId].processId === processId &&
//       processKeysByAchievementId[achievementId].resultId === resultId &&
//       processKeysByAchievementId[achievementId].choiceId === choiceId
//     );
//   };

//   const handleAchievementItemCellClick = achievementId => {
//     if (!isUsingAchievementChip || isUsingEditMode) {
//       return;
//     }

//     handleSetSelectedAchievementId(achievementId);

//     const isAlreadyLinkedAchievement = getIsAlreadyLinkedAchievement(achievementId);
//     const isCurrentLinkedAchievement = getIsCurrentLinkedAchievement(achievementId);

//     // if (isAlreadyLinkedAchievement) {
//     //   handleIsNoticeNewLinkedModalVisible();
//     //   return;
//     // }

//     if (isCurrentLinkedAchievement) {
//       handleDeleteAchievementId();
//       handleIsAchievementModalVisible();
//       return;
//     }

//     handleSetAchievementId(achievementId);
//     handleIsAchievementModalVisible();
//   };

//   const handleNewLinkedAchievementClick = () => {
//     const prevLinkedAchievement = processKeysByAchievementId[selectedAchievementId];

//     handleDeleteAchievementId(prevLinkedAchievement);
//     handleSetAchievementId(selectedAchievementId);
//     handleIsNoticeNewLinkedModalVisible();
//   };

//   const scrollToBottom = () => {
//     const { current } = itemCellContainerRef;

//     if (current) {
//       current.scrollTop = current.scrollHeight;
//     }
//   };

//   useEffect(() => {
//     scrollToBottom();
//   }, [achievement]);

//   useEffect(() => {
//     setTempAchievement(achievement);

//     return () => {
//       if (isAchievementModalVisible) {
//         handleDeleteAllEmptyPropertyAchievement();
//       }
//     };
//   }, [isAchievementModalVisible]);

//   return (
//     <Modal
//       visible={isAchievementModalVisible}
//       size={MODAL_SIZE.BIG}
//       handleCloseButtonClick={handleIsAchievementModalVisible}
//     >
//       {isNoAchievement ? (
//         <NoAchievementContainer>
//           <NoAchievementText>
//             {isUsingAchievementChip && '편집 >'} 업적 추가 버튼을 눌러 새 업적을 만드세요
//           </NoAchievementText>
//         </NoAchievementContainer>
//       ) : (
//         <InnerContainer ref={itemCellContainerRef}>
//           {tempAchievement.map(({ id, title, description, condition }) => (
//             <ItemCell
//               key={id}
//               isUsingEditMode={isUsingEditMode}
//               isUsingAchievementChip={isUsingAchievementChip}
//               achievementId={id}
//               title={title}
//               description={description}
//               condition={condition}
//               processKeysByAchievementId={processKeysByAchievementId}
//               getIsCurrentLinkedAchievement={getIsCurrentLinkedAchievement}
//               handleChangeAchievementText={handleChangeAchievementText}
//               handleDuplicateAchievementClick={handleDuplicateAchievementClick}
//               handleDeleteAchievementClick={handleDeleteAchievementClick}
//               handleSetSelectedAchievementId={handleSetSelectedAchievementId}
//               handleAchievementItemCellClick={handleAchievementItemCellClick}
//               handleIsNoticeDeleteModalVisible={handleIsNoticeDeleteModalVisible}
//             />
//           ))}
//         </InnerContainer>
//       )}

//       <Bottom
//         isUsingAchievementChip={isUsingAchievementChip}
//         isUsingEditMode={isUsingEditMode}
//         achievement={tempAchievement}
//         handleEditModeButtonClick={handleEditModeButtonClick}
//         handleAddAchievementButtonClick={handleAddAchievementButtonClick}
//       />

//       <NoticeDeleteAchievementModal
//         achievementId={selectedAchievementId}
//         isNoticeDeleteModalVisible={isNoticeDeleteModalVisible}
//         handleIsNoticeDeleteModalVisible={handleIsNoticeDeleteModalVisible}
//         handleDeleteAchievementClick={handleDeleteAchievementClick}
//       />

//       <NoticeNewLinkedAchievementModal
//         isNoticeNewLinkedModalVisible={isNoticeNewLinkedModalVisible}
//         handleIsNoticeNewLinkedModalVisible={handleIsNoticeNewLinkedModalVisible}
//         handleNewLinkedAchievementClick={handleNewLinkedAchievementClick}
//       />

//       <SnackBar ref={snackBarRef} width={300} />
//     </Modal>
//   );
// };

// const InnerContainer = styled.div`
//   display: flex;
//   align-items: center;
//   flex-direction: column;

//   width: 100%;
//   height: 452px;

//   overflow-y: scroll;
//   gap: 5px 0px;
//   margin-top: -16px;
//   padding-bottom: 60px;
// `;

// const NoAchievementContainer = styled.div`
//   position: absolute;
//   top: 0;

//   height: 100%;
// `;

// const NoAchievementText = styled(TX.FootnoteM)`
//   color: ${({ theme }) => theme.colors.ui_on_4};

//   height: 100%;
// `;

// export default Achievement;
